import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Output,
  OnInit,
  EventEmitter,
  ChangeDetectorRef,
  Input,
  ViewChild,
  AfterViewInit,
} from '@angular/core';

import { ApiCallService } from '../../Services/api-call-service.service';
import { NotificationService } from '../../Services/notification.service';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
})
export class NotificationsComponent implements OnInit, AfterViewInit {
  @Input() notifications: any[] = [];
  @Input() mode: 'popup' | 'fullpage' = 'popup'; 
  
  @Output() closeNotification = new EventEmitter<void>();
  @Output() notificationsCount = new EventEmitter<number>();
  @Output() openChat = new EventEmitter<void>();

  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  isVisible = true;
  pageNumber = 1;
  pageSize = 10;
  isLoading = false;
  hasMore = true;

  // Real-time state trackers
  activeTab: 'unread' | 'read' = 'unread';
  unreadCount = 0;
  readCount = 0;
  filteredNotifications: any[] = [];

  constructor(
    private eRef: ElementRef,
    private apiCallService: ApiCallService,
    private cdr: ChangeDetectorRef,
    private notificationService: NotificationService,
    private router: Router
  ) { }

  ngOnInit() {
    this.notificationService.notifications$.subscribe((notifs) => {
      // Backend real incoming data mapped directly
      this.notifications = notifs || [];
      this.notificationsCount.emit(this.notifications.length);
      
      this.calculateCounts();
      this.applyTabFilter();
      this.cdr.detectChanges();
    });
  }

  ngAfterViewInit() {
    this.attachScrollListener();
  }

  calculateCounts() {
    this.unreadCount = this.notifications.filter(n => !n.IsRead).length;
    this.readCount = this.notifications.filter(n => n.IsRead).length;
  }

  switchTab(tab: 'unread' | 'read') {
    this.activeTab = tab;
    this.applyTabFilter();
    this.cdr.detectChanges();
    setTimeout(() => this.attachScrollListener(), 50);
  }

  applyTabFilter() {
    if (this.activeTab === 'unread') {
      this.filteredNotifications = this.notifications.filter(n => !n.IsRead);
    } else {
      this.filteredNotifications = this.notifications.filter(n => n.IsRead);
    }
  }

  // Production Ready Mark All As Read Implementation
  markAllNotificationsAsRead() {
    const unreadNotifications = this.notifications.filter(n => !n.IsRead);
    if (unreadNotifications.length === 0) return;

    this.isLoading = true;

    // Har unread notification ke liye API payload parallel calls generate karta hai
    const apiRequests = unreadNotifications.map(notification => {
      return this.apiCallService.PostCallWithToken(
        '',
        `NotificationMessages/ReadMessages?NotificationId=${notification.Id}&Type=${notification.Type}`
      );
    });

    // Sub calls complete hone par single stream trigger hoti hai
    forkJoin(apiRequests).subscribe({
      next: () => {
        // UI locally instantly sync state manage karta hai
        this.notifications.forEach(n => {
          if (!n.IsRead) n.IsRead = true;
        });

        if (this.mode === 'popup') {
          this.notificationService.loadNotifications(true);
        } else {
          this.calculateCounts();
          this.applyTabFilter();
          this.cdr.detectChanges();
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error marking all notifications as read:', err);
        this.isLoading = false;
      }
    });
  }

  private attachScrollListener() {
    if (this.scrollContainer && this.scrollContainer.nativeElement) {
      this.scrollContainer.nativeElement.addEventListener(
        'scroll',
        this.onScroll.bind(this)
      );
    }
  }

  // Exact Context Specific Icons Matching UI Layouts
  getNotificationIcon(type: string): string {
    const formattedType = type?.toLowerCase() || '';
    if (formattedType.includes('player created') || formattedType.includes('playercreated')) {
      return '🎮';
    } else if (formattedType.includes('bonus') || formattedType.includes('score') || formattedType.includes('addscore')) {
      return '🎁';
    } else if (formattedType.includes('info')) {
      return '🔔';
    }
    return '🔔';
  }

  // Accurate Relative Time / Days Ago Calculation Logic
  getDaysAgo(dateString: any): string {
    if (!dateString) return 'just now';
    
    const now = new Date();
    const pastDate = new Date(dateString);
    const msPerMinute = 60 * 1000;
    const msPerHour = msPerMinute * 60;
    const msPerDay = msPerHour * 24;
    
    const elapsed = now.getTime() - pastDate.getTime();
    
    if (elapsed < msPerMinute) {
      return 'just now';
    } else if (elapsed < msPerHour) {
      const minutes = Math.round(elapsed / msPerMinute);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (elapsed < msPerDay) {
      const hours = Math.round(elapsed / msPerHour);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else {
      const days = Math.round(elapsed / msPerDay);
      if (days === 1) return 'a day ago';
      return `${days} days ago`;
    }
  }

  onScroll(event: any) {
    const element = event.target;
    if (
      element.scrollHeight - element.scrollTop <= element.clientHeight + 10 &&
      !this.isLoading &&
      this.hasMore
    ) {
      this.notificationService.loadMoreNotifications();
    }
  }

  onNotificationClick(notification: any) {
    notification.IsRead = true; 
    const notificationId = notification.Id;
    const type = notification.Type;
    let lastWord = '';
    
    if (notification?.Type === 'AddScore' && notification?.Notifications) {
      const words = notification.Notifications.trim().split(/\s+/);
      lastWord = words[words.length - 1];
    }
    
    this.apiCallService
      .PostCallWithToken(
        '',
        `NotificationMessages/ReadMessages?NotificationId=${notificationId}&Type=${type}`
      )
      .subscribe(() => {
        if (this.mode === 'popup') {
          this.notificationService.loadNotifications(true);
        } else {
          this.calculateCounts();
          this.applyTabFilter();
          this.cdr.detectChanges();
        }
      });
      
    this.navigateByType(type, lastWord);

    if (this.mode === 'popup') {
      this.isVisible = false;
      this.closeNotification.emit();
    }
  }

  navigateByType(type: string, gameName: string = '') {
    switch (type.toLowerCase()) {
      case 'wallet': this.router.navigate(['/dashboard/wallet']); break;
      case 'radeem': this.router.navigate(['/dashboard/redeem']); break;
      case 'home': this.router.navigate(['/dashboard/home']); break;
      case 'lottery': this.router.navigate(['/dashboard/lottery']); break;
      case 'addscore':
        localStorage.setItem('GN', gameName);
        this.router.navigate(['/dashboard/credentials']);
        break;
      case 'message': this.openChat.emit(); break;
      default: this.router.navigate(['/dashboard/home']); break;
    }
  }

  removeNotification(index: number, event: Event) {
    event.stopPropagation();   
    event.preventDefault();
    
    const targetItem = this.filteredNotifications[index];
    targetItem.removing = true;

    this.apiCallService
      .PostCallWithToken('', `NotificationMessages/ReadMessages?NotificationId=${targetItem.Id}&Type=${targetItem.Type}`)
      .subscribe();
    
    setTimeout(() => {
      this.notifications = this.notifications.filter(n => n.Id !== targetItem.Id);
      this.calculateCounts();
      this.applyTabFilter();
      this.cdr.detectChanges();
    }, 350);
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    if (this.mode === 'popup' && !this.eRef.nativeElement.contains(event.target)) {
      this.isVisible = false;
      this.closeNotification.emit();
    }
  }
}